import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Compact "Show in user UI" toggle rendered next to a constructor field's
 * label. When checked, the field's key is added to
 * `config.tenantEditableFields`, which the sync maps onto the employee's
 * customization policy so the tenant can see and edit it in their own
 * instance editor. Unchecked = operator-only (the default).
 *
 * Uses a native checkbox to match the rest of the admin-ui (no checkbox
 * primitive / radix-tooltip dependency).
 */
export function FieldExposure({
  exposed,
  onToggle,
  disabled,
  label,
}: {
  exposed: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
  /** Human name of the field, used for the accessible label only. */
  label?: string;
}) {
  const { t } = useTranslation("common");
  return (
    <label
      title={t("fields.exposure.title")}
      className={cn(
        "flex shrink-0 cursor-pointer select-none items-center gap-1.5 text-xs",
        exposed ? "text-primary" : "text-muted-foreground",
        disabled && "cursor-not-allowed opacity-50"
      )}
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5"
        checked={exposed}
        onChange={(e) => onToggle(e.target.checked)}
        disabled={disabled}
        aria-label={t("fields.exposure.ariaLabel", {
          label: label ?? t("fields.exposure.defaultFieldName"),
        })}
      />
      {exposed ? (
        <Eye className="h-3.5 w-3.5" />
      ) : (
        <EyeOff className="h-3.5 w-3.5" />
      )}
      <span>{t("fields.exposure.label")}</span>
    </label>
  );
}
